import type { NoteControllerState } from "../controller/NoteController";
import { NOTE_SYMBOLS } from "../controller/NoteController";
import { layoutBreadcrumbMessage } from "./BreadcrumbMessageLayout";
import { LayoutBitmapTextRenderer } from "./LayoutBitmapTextRenderer";
import {
  SYMBOL_SEQUENCE_FONT_SIZE,
  NOTE_OPTIONS_FONT_KEY,
  NOTE_OPTIONS_FONT_SIZE,
  SYMBOL_SPRITE_FONT_KEY,
  SYMBOL_SEQUENCE_FONT_CONFIGS,
} from "../view/SymbolSequenceFont";

const BG_COLOR = 0x0b3226;
const BG_ALPHA = 0.92;
const COLOR_ACTIVE_NAV = 0x4caf50;
const COLOR_ACTIVE_EDIT = 0xffd54f;
const COLOR_INACTIVE = 0xffffff;
const DEPTH_BG = 100;
const DEPTH_TITLE = 101;
const DEPTH_ROWS = 102;
const DEPTH_HELP = 103;
const TITLE_FONT_KEY = NOTE_OPTIONS_FONT_KEY;
const TITLE_FONT_SIZE = NOTE_OPTIONS_FONT_SIZE;
const TITLE_SCALE = 2;
const OPTIONS_FONT_SIZE = SYMBOL_SEQUENCE_FONT_SIZE;
const OPTIONS_SCALE = 2;
const TITLE_X = 8;
const TITLE_Y = 8;
const GRID_COLS = 2;
const GRID_ROWS = 4;
const COL_GAP = 32;
const ROW_GAP = 8;
const TITLE_BOTTOM_PAD = 16;
const FOOTER_CENTER_Y = 200;
const FOOTER_RESERVED_HEIGHT = 28;
const FOOTER_TEXT =
  "Enter: Edit [A-Z|0-9|Space]  Backspace: Clear";
const SYMBOL_WIDTH = OPTIONS_FONT_SIZE * OPTIONS_SCALE;
const VALUE_GAP = 24;
const NOTE_VALUE_SAMPLE_TEXT = "\u25B6 ??";

export class NoteRenderer {
  private readonly bg: Phaser.GameObjects.Graphics;
  private readonly title: Phaser.GameObjects.BitmapText;
  private readonly helpText: LayoutBitmapTextRenderer;
  private readonly rows: Array<{
    readonly symbol: Phaser.GameObjects.BitmapText;
    readonly value: LayoutBitmapTextRenderer;
    readonly valueX: number;
    readonly y: number;
  }>;
  private readonly viewportWidth: number;
  private readonly viewportHeight: number;

  public constructor(scene: Phaser.Scene) {
    this.viewportWidth = scene.cameras.main.width;
    this.viewportHeight = scene.cameras.main.height;

    this.bg = scene.add.graphics();
    this.bg.setScrollFactor(0);
    this.bg.setDepth(DEPTH_BG);
    this.bg.fillStyle(BG_COLOR, BG_ALPHA);
    this.bg.fillRect(0, 0, this.viewportWidth, this.viewportHeight);

    this.title = scene.add.bitmapText(
      TITLE_X,
      TITLE_Y,
      TITLE_FONT_KEY,
      "Signal Notes",
      TITLE_FONT_SIZE,
    );
    this.title.setScale(TITLE_SCALE);
    this.title.setTint(COLOR_INACTIVE);
    this.title.setScrollFactor(0);
    this.title.setDepth(DEPTH_TITLE);

    this.helpText = new LayoutBitmapTextRenderer(scene, {
      depth: DEPTH_HELP,
      tint: COLOR_INACTIVE,
      fontProfile: SYMBOL_SEQUENCE_FONT_CONFIGS["12px"],
    });
    this.helpText.render(FOOTER_TEXT, {
      centerX: this.viewportWidth / 2,
      y: FOOTER_CENTER_Y,
      verticalAlign: "middle",
    });

    const rowHeight = OPTIONS_FONT_SIZE * OPTIONS_SCALE;
    const valueSampleWidth = layoutBreadcrumbMessage(NOTE_VALUE_SAMPLE_TEXT, {
      maxWidth: 9999,
      asciiAdvance: SYMBOL_SEQUENCE_FONT_CONFIGS["12px"].asciiAdvance,
      spaceAdvance: SYMBOL_SEQUENCE_FONT_CONFIGS["12px"].spaceAdvance,
      fullAdvance: SYMBOL_SEQUENCE_FONT_CONFIGS["12px"].fullAdvance,
      lineHeight: SYMBOL_SEQUENCE_FONT_CONFIGS["12px"].lineHeight,
      renderableChars: SYMBOL_SEQUENCE_FONT_CONFIGS["12px"].renderableChars,
      fallbackChar: SYMBOL_SEQUENCE_FONT_CONFIGS["12px"].fallbackChar,
    }).width * OPTIONS_SCALE;
    const rowTextWidth = SYMBOL_WIDTH + VALUE_GAP + valueSampleWidth;
    const gridHeight = GRID_ROWS * rowHeight + (GRID_ROWS - 1) * ROW_GAP;
    const gridWidth = GRID_COLS * rowTextWidth + (GRID_COLS - 1) * COL_GAP;
    const titleBottom = TITLE_Y + TITLE_FONT_SIZE * TITLE_SCALE + TITLE_BOTTOM_PAD;
    const footerTop = this.viewportHeight - FOOTER_RESERVED_HEIGHT;

    const gridX = Math.floor((this.viewportWidth - gridWidth) / 2);
    const gridY = Math.floor(titleBottom + (footerTop - titleBottom - gridHeight) / 2);

    this.rows = [];
    for (let i = 0; i < NOTE_SYMBOLS.length; i += 1) {
      const col = i % GRID_COLS;
      const row = Math.floor(i / GRID_COLS);
      const x = gridX + col * (rowTextWidth + COL_GAP);
      const y = gridY + row * (rowHeight + ROW_GAP);

      const symbolText = scene.add.bitmapText(x, y, SYMBOL_SPRITE_FONT_KEY, "", OPTIONS_FONT_SIZE);
      symbolText.setScale(OPTIONS_SCALE);
      symbolText.setTint(COLOR_INACTIVE);
      symbolText.setScrollFactor(0);
      symbolText.setDepth(DEPTH_ROWS);

      const valueRenderer = new LayoutBitmapTextRenderer(scene, {
        depth: DEPTH_ROWS,
        tint: COLOR_INACTIVE,
        fontProfile: SYMBOL_SEQUENCE_FONT_CONFIGS["12px"],
        scale: OPTIONS_SCALE,
      });

      this.rows.push({
        symbol: symbolText,
        value: valueRenderer,
        valueX: x + SYMBOL_WIDTH + VALUE_GAP,
        y,
      });
    }
  }

  public render(state: NoteControllerState): void {
    for (let i = 0; i < NOTE_SYMBOLS.length; i += 1) {
      const symbol = NOTE_SYMBOLS[i]!;
      const note = state.notes[symbol] ?? null;
      const value = note ?? "??";
      const row = this.rows[i]!;
      const isActive = i === state.cursorIndex;

      row.symbol.setText(symbol);
      row.value.render(`\u25B6 ${value}`, {
        leftX: row.valueX,
        y: row.y,
      });

      if (isActive) {
        const tint = state.isEditing ? COLOR_ACTIVE_EDIT : COLOR_ACTIVE_NAV;
        row.symbol.setTint(tint);
        row.symbol.setAlpha(1);
        row.value.setTint(tint);
        row.value.setAlpha(1);
      } else {
        const alpha = value === "??" ? 0.5 : 1;
        row.symbol.setTint(COLOR_INACTIVE);
        row.symbol.setAlpha(alpha);
        row.value.setTint(COLOR_INACTIVE);
        row.value.setAlpha(alpha);
      }
    }
  }

  public destroy(): void {
    this.bg.destroy();
    this.title.destroy();
    this.helpText.destroy();
    for (const row of this.rows) {
      row.symbol.destroy();
      row.value.destroy();
    }
    this.rows.length = 0;
  }
}
